import { Message } from 'view-design'
import { PLATFORM } from '@/common/js/constants.js'
import {
  AD_TYPE,
  DEFAULT_SCHEDULE_TIME,
  APP_PROMOTION_TYPE,
  BUDGET_MODE,
  BID_TYPE,
  BUDGET_OPTIMIZE_SWITCH,
  DEEP_BID_TYPE,
  SCHEDULE_TIME_TYPE,
  DELIVERY_MODE,
  BID_VALUE_TYPE,
  LANDING_TYPE,
  AMEME_TYPE,
  IMAGE_MODE,
  BLUE_FLOW_PACKAGE_SETTING_SWITCH,
  DYNAMIC_CREATIVE_SWITCH,
  MARKETING_GOAL,
  MATERIAL_STYLE,
  DELIVERY_TYPE,
  VIDEO_HP_VISIBILITY
} from '../../consts/index'
import { isArray } from '@/common/js/utils.js'
import { useOptimizeGoalRelative } from '../../hooks/useOptimizeGoal.js'
import { MATERIAL_TYPE } from '@/components/material-selector/consts'

export function needDeepRoiGoal(deep_bid_type) {
  const conditions = [DEEP_BID_TYPE.ROI_COEFFICIENT, DEEP_BID_TYPE.FIRST_AND_SEVEN_PAY_ROI, DEEP_BID_TYPE.PER_AND_SEVEN_PAY_ROI].includes(
    deep_bid_type
  )

  return conditions
}
/**
 * @description: 生成基础计划信息
 * @param {*} configForm
 * @return {*}
 */
export function buildBasicPlanRow(configForm) {
  const { project, plan } = configForm

  const inner_plan_params = makeInnerParams(configForm)
  const outer_plan_params = makeExternalParams(configForm)

  if (project.ch_platform_id === PLATFORM.ANDROID) {
    // 安卓应用，非预约投放添加头条分包
    if (project.app_promotion_type !== APP_PROMOTION_TYPE.RESERVE) {
      inner_plan_params.media_pack_switch = project.media_pack_switch
    }
  }

  const creative_params = makeCreativeParams(configForm, outer_plan_params)

  return {
    campaign_params: {
      project_name: project.project_name
    },
    // 辅助参数字段，更换创意素材时需要用到
    plan,
    inner_plan_params,
    outer_plan_params,
    creative_params,
    isCheck: true
  }
}

/**
 * @description: 构造内部计划参数
 * @param {*} configForm
 * @return {*}
 */
function makeInnerParams(configForm) {
  const { project, plan, creatives, targetings } = configForm
  const inner_plan_params = {
    plan_name: project.project_name, // 同项目名称
    package: project.game.ad_package,
    call_type: plan.call_type,
    callback_switch: project.callback_switch,
    ch_platform_id: project.ch_platform_id,
    game_id: project.game.id,
    game_address: project.game_address,
    landing_type: project.landing_type,
    media_pack_switch: project.media_pack_switch,
    monitorPlan: [] // todo
  }

  if (configForm.isV3) {
    inner_plan_params.media_version = 'TOUTIAO3.0'
  } else {
    inner_plan_params.star_task_id = project.star_task?.star_task_id || ''
  }

  // 人群包跨账号推送信息配置
  const audience_push = []
  targetings.forEach((targeting) => {
    if (targeting.accountIds) {
      const { audiences } = targeting.targetingParamJson
      const audiencesList = Object.values(audiences)
      if (audiencesList?.length) {
        const ids = []
        audiencesList.forEach((item) => {
          ids.push(...item.excludes, ...item.includes)
        })
        if (ids.length) {
          audience_push.push({
            from_account_id: Number(targeting.accountIds),
            audience_id: ids.join(',')
          })
        }
      }
    }
  })
  if (audience_push.length > 0) {
    inner_plan_params.audience_push = audience_push
  }

  // 图文素材跨账号推送信息配置
  const carousel_push = []
  creatives.forEach((creative) => {
    const { imageAudio } = creative.selectedMaterials
    imageAudio.forEach((item) => {
      carousel_push.push({
        from_account_id: item.imageAudioAccount.id,
        carousel_id: item.id
      })
    })
  })

  if (carousel_push.length > 0) {
    inner_plan_params.carousel_push = carousel_push
  }

  return inner_plan_params
}

/**
 * @description: 构造创意参数
 * @param {*} configForm
 * @param {*} outer_plan_params
 * @return {*}
 */
function makeCreativeParams(configForm, outer_plan_params) {
  const { project, plan } = configForm
  const { isAll, isSearch, isDuration, isNormal } = useOptimizeGoalRelative(project)
  const isStarTask = !!project.star_task?.star_task_id
  const autoMaterialEnable = !configForm.isV3 && isStarTask && project.star_auto_material_addition_switch === 'ON'
  const isLiveMaterial = plan.materials_type === MATERIAL_STYLE.LIVE_MATERIALS
  function getPlanBidParams(bid_type, cpa_bid, bid_num_range) {
    const params = { bid_type }

    if (bid_type === BID_VALUE_TYPE.FIXED) {
      params.cpa_bid = Number(cpa_bid)
    }
    if (bid_type === BID_VALUE_TYPE.RANGE) {
      params.bid_range = 1
      params.bid_num_range = bid_num_range.map(Number)
    }
    return params
  }

  function getCommonCreativeParams() {
    const params = {
      video_material_list: [],
      image_material_list: [],
      product_info: {
        titles: [plan.app_name],
        image_ids: [plan.app_image.id],
        selling_points: plan.selling_points_list
      },
      call_to_action_buttons: plan.call_to_action_buttons_list,
      intelligent_generation: plan.intelligent_generation,
      is_comment_disable: plan.is_comment_disable,
      ad_download_status: plan.ad_download_status
    }

    // 关联星光联投任务且开启自动优选素材，开启投放原视频标题
    if (isStarTask && autoMaterialEnable) {
      params.original_video_title = plan.original_video_title
    }
    return params
  }

  // 广告创意和出价信息
  let creative_params = {
    campaign_name: '',
    video_material_list: [],
    image_material_list: [],
    awemeIdentity: plan.awemeIdentity,
    // materials_type: plan.materials_type,
    source: plan.source?.length ? plan.source.trim() : undefined,
    product_info: {
      titles: [plan.app_name],
      image_ids: [plan.app_image.id],
      selling_points: plan.selling_points_list
    },
    call_to_action_buttons: plan.call_to_action_buttons_list,
    intelligent_generation: plan.intelligent_generation,
    is_comment_disable: plan.is_comment_disable,
    ad_download_status: plan.ad_download_status,
    // 品牌名称，广告类别
    brand_info: {}
  }

  // 营销场景为直播时设置素材形式参数；营销场景为短视频图文时，不用设置任何参数
  if (!configForm.isV3 && project.marketing_goal === MARKETING_GOAL.LIVE) {
    creative_params.materials_type = plan.materials_type
  }

  // 批量复制、修改流程plan.web_url_material_list会存在
  if (plan.web_url_material_list?.length) {
    creative_params.web_url_material_list = plan.web_url_material_list
  }

  // 原生广告设置：抖音号
  if (plan.awemeIdentity === AMEME_TYPE.DOUYIN) {
    creative_params.native_setting = {
      aweme_id: plan.aweme_id,
      aweme_name: plan.aweme_info?.aweme_name || plan.aweme_id,
      video_hp_visibility: plan.video_hp_visibility || VIDEO_HP_VISIBILITY.ON,
      anchor_related_type: plan.anchor_related_type
    }
  }

  // 原生广告设置：达人视频
  if (plan.awemeIdentity === AMEME_TYPE.AWEME_VIDEO) {
    const native_setting = {
      anchor_related_type: plan.anchor_related_type
    }

    // 关联星光联投任务且开启自动优选素材，须设置抖音号设置类型
    if (isStarTask && autoMaterialEnable) {
      native_setting.aweme_setting_type = plan.aweme_setting_type || 'ALL'
    }

    creative_params.native_setting = native_setting
  }
  if (isArray(plan.yuntu_category_id) && plan.yuntu_category_id.length) {
    // 级联选择器取path最后一个id
    creative_params.brand_info.yuntu_category_id = Number(plan.yuntu_category_id.slice(-1))
  }
  if (plan.cdp_brand_id) {
    creative_params.brand_info.cdp_brand_id = plan.cdp_brand_id
    // creative_params.brand_info.cdp_brand_id = 4020171
    // creative_params.brand_info.brand_name_id = 11524109
  }
  if (configForm.isV3 && plan.brand_name_id) {
    creative_params.brand_info.brand_name_id = plan.brand_name_id
  }

  // 通投广告
  if (isAll.value) {
    creative_params = Object.assign({}, creative_params, getCommonCreativeParams())

    if (project.delivery_mode === DELIVERY_MODE.MANUAL) {
      creative_params.budget = Number(plan.budget)
      // 开启预算择优分配，budget仅允许传0或传空
      if (outer_plan_params.delivery_setting.budget_optimize_switch === BUDGET_OPTIMIZE_SWITCH.ON) {
        creative_params.budget = 0
      }
      if (project.delivery_setting.bid_type === BID_TYPE.NO_BID) {
        // todo
      } else {
        //  手动投放模式，非最大转化出价，为广告设置出价
        const { bid_type, cpa_bid, bid_num_range } = plan
        creative_params = Object.assign({}, creative_params, getPlanBidParams(bid_type, cpa_bid, bid_num_range))

        if (needDeepRoiGoal(outer_plan_params.delivery_setting.deep_bid_type)) {
          const { roi_bid_type, roi_bid_num_range } = plan
          creative_params.roi_bid_type = roi_bid_type
          if (roi_bid_type === BID_VALUE_TYPE.FIXED) {
            creative_params.roi_goal = Number(plan.roi_goal)
          }
          if (roi_bid_type === BID_VALUE_TYPE.RANGE) {
            creative_params.roi_bid_range = 1
            creative_params.roi_bid_num_range = roi_bid_num_range.map(Number)
          }
        }
      }
    }
  }

  // 直播场景的直播素材形式，单独构建creative_params参数结构
  if (isLiveMaterial) {
    const { campaign_name, awemeIdentity, brand_info, native_setting } = creative_params
    const liveMaterialParams = {
      campaign_name,
      image_material_list: [],
      awemeIdentity,
      materials_type: MATERIAL_STYLE.LIVE_MATERIALS,
      native_setting: {
        aweme_id: native_setting.aweme_id,
        aweme_name: native_setting.aweme_name,
        video_hp_visibility: native_setting.video_hp_visibility
      },
      brand_info
    }

    // 手动投放的直播素材根据通投的广告参数来
    if (project.delivery_mode === DELIVERY_MODE.MANUAL) {
      const { budget, bid_num_range, bid_range, cpa_bid, roi_bid_type, roi_goal, roi_bid_range, roi_bid_num_range } = creative_params
      if (budget) liveMaterialParams.budget = budget
      if (cpa_bid) liveMaterialParams.cpa_bid = cpa_bid
      if (bid_num_range?.length) liveMaterialParams.bid_num_range = bid_num_range
      if (bid_range) liveMaterialParams.bid_range = bid_range
      if (roi_bid_type) liveMaterialParams.roi_bid_type = roi_bid_type
      if (roi_goal) liveMaterialParams.roi_goal = roi_goal
      if (roi_bid_range) liveMaterialParams.roi_bid_range = roi_bid_range
      if (roi_bid_num_range?.length) liveMaterialParams.roi_bid_num_range = roi_bid_num_range
    }

    return liveMaterialParams
  }

  // 搜索广告
  if (isSearch.value) {
    // 文本摘要
    if (plan.text_abstract_list?.length) {
      creative_params.text_abstract_list = plan.text_abstract_list.map((abstract_text) => ({ abstract_text }))
    }
    // 关键词
    // if (plan.keywords?.length) {
    //   creative_params.keywords = plan.keywords
    // }

    // 投放ad_type = SEARCH搜索广告、常规投放、蓝海流量项目时，只支持传入项目蓝海流量包下的一个蓝海关键词，不支持设置其他关键词
    // if (isBlueFlowEnable.value) {
    //   if (!plan.blue_flow_keywords) throw new Error('蓝海流量关键词不能为空')
    //   // 当项目为蓝海流量时，匹配类型只支持传入EXTENSIVE（广泛匹配），传入其他不生效。
    //   creative_params.keywords = [{ word: plan.blue_flow_keywords, match_type: 'EXTENSIVE' }]
    // }

    if (isNormal.value) {
      creative_params = Object.assign({}, creative_params, getCommonCreativeParams())

      // 广告预算
      creative_params.budget = Number(plan.budget)
      // 品牌名称，广告类别
      creative_params.brand_info = {}
      if (plan.yuntu_category_id) {
        creative_params.brand_info.yuntu_category_id = Number(plan.yuntu_category_id)
      }
      if (plan.cdp_brand_id) {
        creative_params.brand_info.cdp_brand_id = plan.cdp_brand_id
      }
      if (configForm.isV3 && plan.brand_name_id) {
        creative_params.brand_info.brand_name_id = plan.brand_name_id
      }
      // 动态创意
      if (plan.dynamic_creative_switch === DYNAMIC_CREATIVE_SWITCH.ON) {
        creative_params.dynamic_creative_switch = plan.dynamic_creative_switch
        creative_params.advanced_dc_settings = plan.advanced_dc_settings
      }
    }

    if (isDuration.value) {
      // 搜索广告极速智投不需要锚点验证
      if (creative_params.native_setting) {
        creative_params.native_setting.anchor_related_type = undefined
      }
    }

    // 智能拓流
    // creative_params.auto_extend_traffic = plan.auto_extend_traffic || AUTO_EXTEND_TRAFFIC_SWITCH.OFF
  }

  if (configForm.isV3) {
    delete creative_params.intelligent_generation
    delete creative_params.ad_download_status
    delete creative_params.dynamic_creative_switch
    delete creative_params.advanced_dc_settings
    delete creative_params.materials_type
    creative_params.aigc_dynamic_creative_switch = plan.aigc_dynamic_creative_switch
  }

  return creative_params
}

/**
 * @description: 构造排期与预算出价参数
 * @param {*} configForm
 * @return {*}
 */
function makeDeliverySetting(configForm) {
  const { project } = configForm
  const {
    schedule_type,
    bid_type,
    bid_value_type,
    roi_bid_value_type,
    cpa_bid,
    first_roi_goal,
    bid_num_range,
    roi_bid_num_range,
    schedule_time_type,
    schedule_time,
    budget_mode,
    budget,
    pricing,
    budget_optimize_switch,
    date_range,
    roi_goal,
    optimize_goal
  } = project.delivery_setting

  const [start_time, end_time] = date_range || [undefined, undefined]
  const delivery_setting = {
    schedule_type,
    schedule_time: DEFAULT_SCHEDULE_TIME,
    start_time,
    end_time,
    bid_type,
    budget_mode
  }

  if (configForm.isV3) {
    delivery_setting.pricing = pricing
  }

  const { isProcedural, isSearch, isDuration, isFirstRoiGoal } = useOptimizeGoalRelative(project)

  if (isDuration.value) {
    if (bid_type !== BID_TYPE.CUSTOM) {
      Message.error('搜索广告极速智投竞价策略只支持稳定成本')
      throw new Error('搜索广告极速智投竞价策略只支持稳定成本')
    }
  }

  // 搜索广告常规投放、极速智投 且竞价策略为稳定成本，为项目设置出价
  // 自动投放且竞价策略为稳定成本，为项目设置出价
  if ((isProcedural.value || isSearch.value) && bid_type === BID_TYPE.CUSTOM) {
    // 首日ROI+7日ROI 单独设置为first_roi_goal
    if (isFirstRoiGoal.value) {
      delivery_setting.first_roi_goal = Number(first_roi_goal)
    } else {
      if (bid_value_type === BID_VALUE_TYPE.FIXED) {
        delivery_setting.cpa_bid = Number(cpa_bid)
      }
      if (bid_value_type === BID_VALUE_TYPE.RANGE) {
        delivery_setting.bid_range = 1
        delivery_setting.bid_num_range = bid_num_range.map(Number)
      }
    }

    if (needDeepRoiGoal(optimize_goal.deep_bid_type)) {
      if (roi_bid_value_type === BID_VALUE_TYPE.FIXED) {
        delivery_setting.roi_goal = Number(roi_goal)
      }

      if (roi_bid_value_type === BID_VALUE_TYPE.RANGE) {
        delivery_setting.roi_bid_range = 1
        delivery_setting.roi_bid_num_range = roi_bid_num_range.map(Number)
      }
    }
  }

  // 极速智投为项目设置出价，
  // if (isDuration.value) {
  //   delivery_setting = {
  //     ...delivery_setting,
  //     ...getBidDeepBid(),
  //     // ad_type = SEARCH & delivery_type = DURATION 即投放搜索极速智投广告时，仅支持CUSTOM（传入其他值不会生效）
  //     bid_type: BID_TYPE.CUSTOM
  //   }
  // }

  // 当bid_type=NO_BID，处理cbo预算择优分配
  if (bid_type === BID_TYPE.NO_BID) {
    if (budget_mode === BUDGET_MODE.DAY) {
      delivery_setting.budget_optimize_switch = budget_optimize_switch
    }

    if (isProcedural.value) {
      // 当delivery_mode = PROCEDURAL &&landing_type = APP/MICRO_GAME时不支持OFF
      if ([LANDING_TYPE.MICRO_GAME, LANDING_TYPE.APP].includes(project.landing_type)) {
        delivery_setting.budget_optimize_switch = BUDGET_OPTIMIZE_SWITCH.ON
      }
    }
  }

  // 深度优化方式不等于无时，传入深度优化方式参数
  if (optimize_goal.deep_bid_type !== DEEP_BID_TYPE.DEFAULT) {
    delivery_setting.deep_bid_type = optimize_goal.deep_bid_type
  }

  if (schedule_time_type === SCHEDULE_TIME_TYPE.RANGE) {
    delivery_setting.schedule_time = schedule_time
  }

  if (budget_mode === BUDGET_MODE.DAY) {
    delivery_setting.budget = Number(budget)
  }

  return delivery_setting
}

/**
 * @description: 构造外部计划参数
 * @param {*} configForm
 * @return {*}
 */
function makeExternalParams(configForm) {
  const { project } = configForm
  const { optimize_goal, schedule_time_type } = project.delivery_setting
  const outer_plan_params = {
    landing_type: project.landing_type,
    marketing_goal: project.marketing_goal,
    ad_type: project.ad_type,
    delivery_range: {}, // 版位信息，要从定向包内取出
    delivery_setting: makeDeliverySetting(configForm),
    download_url: '',
    deep_external_action: '',
    external_action: optimize_goal.external_action,
    timeRange: schedule_time_type === SCHEDULE_TIME_TYPE.ALL ? 'NONE' : SCHEDULE_TIME_TYPE.RANGE
  }

  const { isSearch, isNormal } = useOptimizeGoalRelative(project)

  if (!configForm.isV3) {
    outer_plan_params.app_promotion_type = project.app_promotion_type
  }
  // 旧版星广联投任务才支持自动优选素材。
  if (!configForm.isV3 && project.star_task?.star_task_id && project.star_auto_material_addition_switch === 'ON') {
    outer_plan_params.star_auto_material_addition_switch = 'ON'
  }

  // 投放模式根据广告类型传入
  if (configForm.isV3 || project.ad_type === AD_TYPE.ALL) {
    outer_plan_params.delivery_mode = project.delivery_mode
  }

  if (configForm.isV3) {
    outer_plan_params.audience_type = project.audience_type
    outer_plan_params.delivery_type = DELIVERY_TYPE.NORMAL
  }
  if (!configForm.isV3 && isSearch.value) {
    outer_plan_params.delivery_type = project.delivery_type
  }

  // 搜索广告下常规投放：蓝海流量投放
  if (isNormal.value) {
    const { blue_flow_package_setting, blue_flow_package_id } = project.blue_flow_package || {}
    if (blue_flow_package_setting === BLUE_FLOW_PACKAGE_SETTING_SWITCH.ON && blue_flow_package_id) {
      outer_plan_params.blue_flow_package = project.blue_flow_package
    }
  }

  // ios 应用下载如果配置了子链接，download_url
  if (project.ch_platform_id === PLATFORM.IOS) {
    if (project.app_promotion_type === APP_PROMOTION_TYPE.DOWNLOAD && project.download_url) {
      outer_plan_params.download_url = project.download_url?.trim()
    }
  }

  if (project.ch_platform_id === PLATFORM.MINI_PROGRAM) {
    if (configForm.isV3) {
      outer_plan_params.delivery_medium = project.micro_promotion_type
      outer_plan_params.instance_id = String(project.micro_app_instance_id || '').trim()
    } else {
      outer_plan_params.micro_promotion_type = project.micro_promotion_type
      outer_plan_params.micro_app_instance_id = project.micro_app_instance_id?.trim() || ''
    }
  }

  if (project.app_promotion_type === APP_PROMOTION_TYPE.RESERVE) {
    outer_plan_params.subscribe_url = project.subscribe_url
  }
  if (needDeepRoiGoal(optimize_goal.deep_bid_type)) {
    const { deep_external_action } = optimize_goal
    outer_plan_params.deep_external_action = deep_external_action
  }

  // 搜索快投
  if (project.keywordSwitch) {
    if (project.search_bid_ratio) {
      outer_plan_params.search_bid_ratio = project.search_bid_ratio
    }
    if (project.keywords?.length) {
      outer_plan_params.keywords = project.keywords
    }
    outer_plan_params.audience_extend = project.audience_extend
  }

  return outer_plan_params
}

/**
 * @description: 创意素材图片、视频处理
 * @param {*} material
 * @return {*}
 */
export function getCreativeImageMode(material) {
  const { width, height, type } = material

  const imageItem = {}

  switch (type) {
    case MATERIAL_TYPE.IMAGE: {
      const imageId = `@${material.id}`
      // 竖版大图
      if (height > width && width >= 720 && height >= 1280) {
        imageItem.image_mode = IMAGE_MODE.LARGE_VERTICAL
        imageItem.images = [{ image_id: imageId }]
      } else if (width > height && width >= 1280 && height >= 720) {
        // 横版大图
        imageItem.image_mode = IMAGE_MODE.LARGE
        imageItem.images = [{ image_id: imageId }]
      } else if (width >= 456 && height >= 300) {
        // 小图
        imageItem.image_mode = IMAGE_MODE.SMALL
        imageItem.images = [{ image_id: imageId }]
      }
      break
    }

    case MATERIAL_TYPE.VIDEO:
      imageItem.video_cover_id = `@${material.cover_id}`
      // 竖版视频
      if (height > width && width >= 720 && height >= 1280) {
        imageItem.image_mode = IMAGE_MODE.VIDEO_VERTICAL
        imageItem.video_id = `@${material.id}`
      } else if (width > height && width >= 1280 && height >= 720) {
        // 横版视频
        imageItem.image_mode = IMAGE_MODE.VIDEO
        imageItem.video_id = `@${material.id}`
      }

      break

    default:
      break
  }

  return imageItem
}
